"use client";

import { useTheme } from "next-themes";
import { useTranslations } from "next-intl";
import { startTransition, useEffect, useState } from "react";
import { cn } from "@/lib/utils";

/** Matches Nav `LangSwitch` outer track: p-0.5 + min-h-8 links → 2.25rem total height. */
const toggleChrome =
  "inline-flex h-9 w-9 shrink-0 items-center justify-center rounded-[var(--radius-sm)] border border-[color-mix(in_srgb,var(--outline-variant)_18%,transparent)] bg-[color-mix(in_srgb,var(--surface-container-high)_82%,var(--design-ambient-tint)_18%)] text-[var(--on-surface-variant)] hover:text-[var(--on-surface)] touch-manipulation transition-colors duration-200";

export function ThemeToggle({ className }: { className?: string }) {
  const { resolvedTheme, setTheme } = useTheme();
  const [mounted, setMounted] = useState(false);
  const t = useTranslations("nav");

  useEffect(() => {
    startTransition(() => setMounted(true));
  }, []);

  if (!mounted) {
    return (
      <div className={cn(toggleChrome, className)} aria-hidden />
    );
  }

  const isDark = resolvedTheme === "dark";

  return (
    <button
      type="button"
      onClick={() => setTheme(isDark ? "light" : "dark")}
      className={cn(
        toggleChrome,
        "font-body focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[color-mix(in_srgb,var(--primary)_70%,var(--design-accent-pop)_30%)] focus-visible:ring-offset-1 focus-visible:ring-offset-[color-mix(in_srgb,var(--surface-container-high)_88%,var(--design-ambient-tint)_12%)]",
        className,
      )}
      aria-label={isDark ? t("switchToLight") : t("switchToDark")}
    >
      {isDark ? <SunIcon className="size-4" /> : <MoonIcon className="size-4" />}
    </button>
  );
}

function SunIcon({ className }: { className?: string }) {
  return (
    <svg
      className={className}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden
    >
      <circle cx="12" cy="12" r="4" />
      <path d="M12 2v2M12 20v2M4.93 4.93l1.41 1.41M17.66 17.66l1.41 1.41M2 12h2M20 12h2M6.34 17.66l-1.41 1.41M19.07 4.93l-1.41 1.41" />
    </svg>
  );
}

function MoonIcon({ className }: { className?: string }) {
  return (
    <svg
      className={className}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden
    >
      <path d="M12 3a6 6 0 0 0 9 9 9 9 0 1 1-9-9Z" />
    </svg>
  );
}
